import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type CommunityRecord,
  type CommunityProfilePatch,
  CommunityViewPrivate,
  CommunityViewPublic,
} from "@bbb/model";
import {
  fetchLiveCommunities,
  fetchMyCommunities,
  updateCommunityProfile,
  requestCommunityLogoUploadUrl,
  completeCommunityLogoUpload,
  initiateCommunityCreation,
  completeCommunityCreation,
} from "./apiClients";
import { queryKeys } from "./queryClient";
import { Avatar } from "@bbb/model";
import { useAuthStore } from "@stores/useAuthStore";

async function requireAuthToken(): Promise<string> {
  return useAuthStore.getState().getSessionAuthToken();
}

/**
 * Fetch communities for the Community Owner
 * @param enabled
 * @returns
 */
export function usePrivateCommunitiesQuery(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.privateCommunities(),
    enabled,
    queryFn: async () => {
      const token = await requireAuthToken();
      return fetchMyCommunities(token);
    },
  });
}

async function dataUrlToBlob(dataUrl: string) {
  const response = await fetch(dataUrl);
  if (!response.ok) {
    throw new Error("Failed to read logo data");
  }
  return response.blob();
}

export async function uploadCommunityLogoFromDataUrl(
  communityId: string,
  dataUrl: string
): Promise<Avatar> {
  const token = await requireAuthToken();
  const blob = await dataUrlToBlob(dataUrl);
  const contentType = blob.type || "image/png";
  const uploadMeta = await requestCommunityLogoUploadUrl(token, communityId, {
    contentType,
  });

  if (blob.size > uploadMeta.maxBytes) {
    throw new Error("Logo must be smaller than the allowed upload size");
  }

  const uploadResponse = await fetch(uploadMeta.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
      "x-goog-content-length-range": `0,${uploadMeta.maxBytes}`,
    },
    body: blob,
  });

  if (!uploadResponse.ok) {
    throw new Error("Logo upload failed. Please try again.");
  }

  const completion: CommunityViewPrivate = await completeCommunityLogoUpload(
    token,
    communityId,
    {
      objectPath: uploadMeta.objectPath,
    }
  );

  const logo = completion?.profile?.logo;

  return logo;
}

async function fetchAllPublicCommunities(params?: { limit?: number }) {
  const pageSize = params?.limit;
  const communities: CommunityViewPublic[] = [];
  let cursor: string | undefined;

  // Fetch pages until no cursor is returned so the store has the full list.
  while (true) {
    const page = await fetchLiveCommunities(null, {
      limit: pageSize,
      cursor,
    });

    communities.push(...(page?.items ?? []));

    const nextCursor = page?.nextCursor;
    if (!nextCursor) {
      break;
    }

    cursor = nextCursor;
  }

  return communities;
}

export function usePublicCommunitiesQuery(
  params?: { limit?: number },
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: queryKeys.allLiveCommunities(params),
    enabled,
    queryFn: () => fetchAllPublicCommunities(params),
  });
}

export function useUpsertCommunityMutation(
  options?: UseMutationOptions<CommunityRecord, unknown, CommunityProfilePatch>
) {
  const queryClient = useQueryClient();

  return useMutation<CommunityRecord, unknown, CommunityProfilePatch>({
    ...options,
    mutationFn: async (variables) => {
      const token = await requireAuthToken();
      const { id, ...profilePatch } = variables;

      if (!id) {
        throw new Error("Community id is required to update the profile");
      }

      return updateCommunityProfile(token, id, profilePatch);
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(queryKeys.theCommunity(data.id), data);
      // Keep the owned communities query in sync so the store rehydrate effect
      // doesn't overwrite the latest mutation result with stale cache data.
      queryClient.setQueryData(
        queryKeys.privateCommunities(),
        (prev: CommunityRecord[] | undefined) => {
          if (!prev) return [data];
          const idx = prev.findIndex((c) => c.id === data.id);
          if (idx === -1) return [...prev, data];
          return prev.map((c) => (c.id === data.id ? data : c));
        }
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.privateCommunities(),
      });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, undefined as never);
      }
    },
  });
}

type CreateCommunityVariables = {
  name: string;
  logo: Avatar | string;
};

export function useCreateCommunityMutation(
  options?: UseMutationOptions<
    CommunityRecord,
    unknown,
    CreateCommunityVariables
  >
) {
  const queryClient = useQueryClient();

  return useMutation<CommunityRecord, unknown, CreateCommunityVariables>({
    ...options,
    mutationFn: async (variables) => {
      const token = await requireAuthToken();

      const logoDataUrl = variables.logo as string | undefined;
      if (!logoDataUrl || typeof logoDataUrl !== "string") {
        throw new Error("Logo is required to create a community");
      }

      const blob = await dataUrlToBlob(logoDataUrl);
      const contentType =
        blob.type ||
        (logoDataUrl.startsWith("data:") &&
          logoDataUrl.split(";")[0].slice(5)) ||
        "image/png";

      const init = await initiateCommunityCreation(token, { contentType });

      if (blob.size > init.upload.maxBytes) {
        throw new Error("Logo must be smaller than the allowed upload size");
      }

      const uploadResponse = await fetch(init.upload.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
          "x-goog-content-length-range": `0,${init.upload.maxBytes}`,
        },
        body: blob,
      });

      if (!uploadResponse.ok) {
        throw new Error("Logo upload failed. Please try again.");
      }

      return completeCommunityCreation(token, init.communityId, {
        name: variables.name,
        objectPath: init.upload.objectPath,
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(queryKeys.theCommunity(data.id), data);
      queryClient.invalidateQueries({
        queryKey: queryKeys.privateCommunities(),
      });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, undefined as never);
      }
    },
  });
}
